import { useState, useEffect, useReducer, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../../shared/context/auth-context';
import { HttpRequestError, useHttpClient } from '../../shared/hooks/http-hook';
import { normalizeFromApi } from '../serialization/normalizeFromApi';
import { transformAssessmentForApi } from '../serialization/transformForApi';
import { ValidationContext } from '../validation/ValidationContext';

const pageReducer = (state, action) => {
  switch (action.type) {
    case 'MEDIA':
      return {
        ...state,
        currentMedia: action.media,
      };
    case 'PAGE':
      return {
        ...state,
        currentPage: action.title,
      };
    case 'PROGRESS':
      return {
        ...state,
        assessmentProgress: action.progress,
      };
    case 'SECTION':
      return {
        ...state,
        sectionNo: action.no,
        sectionMax: action.max,
      };
    default:
      return state;
  }
};

const assessmentReducer = (state, action) => {
  switch (action.type) {
    case 'date':
      return {
        ...state,
        [action.id]: action.value,
      };
    case 'DELETE_KEY':
      const newState = { ...state };
      delete newState[action.id];
      return newState;

    default:
      return {
        ...state,
        [action.id]: action.value,
      };
  }
};

export function useAssessment(props) {
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { triggerValidation } = useContext(ValidationContext);

  const [regionContent, setRegionContent] = useState();
  const [errorSummary, setErrorSummary] = useState([]);
  const [assessmentId, setAssessmentId] = useState();
  const [repeatAssessment, setRepeatAssessment] = useState(false);
  const [mobileOnly, setMobileOnly] = useState(false);
  const [isPlatformMismatch, setIsPlatformMismatch] = useState(false);
  const [isRetakeEnabled, setIsRetakeEnabled] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalTextContent, setModalTextContent] = useState('');
  const [scrollPositions, setScrollPositions] = useState({});
  // Focus error summary when errors are present
  useEffect(() => {
    if (errorSummary.length > 0) {
      const errorElement = document.getElementById('error_summary');
      if (errorElement) {
        errorElement.focus();
      }
    }
  }, [errorSummary]);

  const pageList = [
    {
      title: 'AssessmentProfile',
      media: 'Profile_Setup',
      sectionNo: 0,
      sectionMax: 0,
    },
    {
      title: 'PhysicalHealth1',
      media: 'PhysicalHealth',
      sectionNo: 0,
      sectionMax: 0,
    },
    {
      title: 'PhysicalActivity1',
      media: 'PhysicalActivity',
      sectionNo: 0,
      sectionMax: 0,
    },
    { title: 'Sleep1', media: 'Sleep', sectionNo: 0, sectionMax: 0 },
    { title: 'Stress1', media: 'MentalHealth', sectionNo: 0, sectionMax: 0 },

    {
      title: 'MentalHealth2',
      media: 'MentalHealth',
      sectionNo: 1,
      sectionMax: 4,
    },
    {
      title: 'MentalHealth3',
      media: 'MentalHealth',
      sectionNo: 2,
      sectionMax: 4,
    },
    {
      title: 'MentalHealth4',
      media: 'MentalHealth',
      sectionNo: 3,
      sectionMax: 4,
    },
    {
      title: 'MentalHealth5',
      media: 'MentalHealth',
      sectionNo: 4,
      sectionMax: 4,
    },
    {
      title: 'FinancialWellbeing1',
      media: 'FinancialWellbeing',
      sectionNo: 0,
      sectionMax: 0,
    },
    { title: 'Nutrition1', media: 'Nutrition', sectionNo: 0, sectionMax: 0 },
    { title: 'Hydration1', media: 'Hydration', sectionNo: 0, sectionMax: 0 },
    { title: 'Alcohol1', media: 'Alcohol', sectionNo: 0, sectionMax: 0 },
    {
      title: 'Energy1',
      media: 'Energy',
      sectionNo: auth.access === '2' ? 0 : 1,
      sectionMax: auth.access === '2' ? 0 : 3,
    },
    {
      title: 'Energy2',
      media: 'Energy',
      sectionNo: 2,
      sectionMax: 3,
      conditional: true,
    },
    {
      title: 'Energy3',
      media: 'Energy',
      sectionNo: 3,
      sectionMax: 3,
      conditional: true,
    },
  ];

  const [assessmentValues, dispatchValues] = useReducer(assessmentReducer, {});

  const saveAssessmentValue = useCallback(
    (props) => {
      // check for empty values first and remove them from the object
      if (props.value === null || props.value === undefined || props.value.length === 0) {
        if (props.id in assessmentValues) {
          dispatchValues({ type: 'DELETE_KEY', id: props.id });
        }
        return;
      }

      if (props.id && (props.value || props.value === 0)) {
        dispatchValues({
          type: props.id,
          id: props.id,
          value: props.value,
        });
      }
    },
    [assessmentValues]
  );

  const [pageOptions, dispatch] = useReducer(pageReducer, {
    assessmentProgress: 12,
    sectionNo: 0,
    sectionMax: 0,
    currentMedia: 'Profile_Setup',
    currentPage: 'AssessmentProfile',
  });

  const saveAssessmentDataHandler = async (dir: string, onRestart?: () => void) => {
    const source = 'web';
    if (assessmentValues && auth.userId && assessmentId && auth.token) {
      const pageNo = assessmentValues.iProgress;
      if (auth.access === '2' && pageNo === 13) {
        assessmentValues.iProgress = 15;
      } else if (pageNo === 15) {
        assessmentValues.iProgress = 15;
      }

      const wireData = transformAssessmentForApi(assessmentValues);

      let responseData;
      try {
        responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/assessments/update`,
          'POST',
          JSON.stringify({
            source,
            userid: auth.userId,
            assessmentid: assessmentId,
            data: wireData,
          })
        );
      } catch (err) {
        if (err instanceof HttpRequestError && err.errorCode === 'RESTART_NEEDED') {
          onRestart?.();
        }
        if (err instanceof HttpRequestError && err.errorCode === 'PLATFORM_MISMATCH') {
          setIsPlatformMismatch(true);
        }
        throw err;
      }
      if (
        responseData &&
        responseData.status === 'complete' &&
        dir !== 'prev' &&
        assessmentValues.iProgress === 15
      ) {
        auth.tagHandler(responseData.tags);
        let url = '/for-you/?referer=assessment';
        if (repeatAssessment) {
          url = '/for-you/?referer=repeat_assessment';
        }
        navigate(url);
      }
    }
  };

  const navigateNextPageHandler = async (dir, pi = 1) => {
    setErrorSummary([]);
    // find current page position
    const { currentPage } = pageOptions;
    const { currentMedia } = pageOptions;
    if (dir !== 'prev' && dir !== 'load') {
      // is current page valid
      // check for registration
      if (currentPage === 'AssessmentProfile' && !auth.isLoggedIn) {
        // cant pass register until logged in
        return;
      }

      // validate
      triggerValidation();
      const questions = Array.from(
        document.querySelectorAll(`#${currentPage} .req fieldset`)
      ).filter((q) => q.id);
      let valid = true;
      for (let q = 0; q < questions.length; q++) {
        const questionid = questions[q].id;

        if (
          assessmentValues[questionid] === null ||
          assessmentValues[questionid] === undefined ||
          assessmentValues[questionid].length === 0
        ) {
          setErrorSummary((prev) => [...prev, questionid]);
          valid = false;
        }
      }
      if (!valid) {
        return;
      }
      setErrorSummary([]);
    }
    if (dir !== 'load') {
      try {
        await saveAssessmentDataHandler(dir, () => navigateNextPageHandler('load', 0));
      } catch {
        return;
      }
    }
    let i = pageList.findIndex((p) => p.title === currentPage);
    if (dir === 'load') {
      if (Number(pi) === 15) {
        i = 14;
      } else {
        i = Number(pi);
      }
    }
    if (dir === 'prev') {
      i--;
    } else {
      i++;
    }
    // prevent cliff edge and block back to register
    if (i < 0) {
      i = 0;
    }
    if (i > pageList.length) {
      i = pageList.length - 1;
    }

    const skipToEnd = i > 15 || (i > 13 && auth.access === '2');

    if (!skipToEnd) {
      // continue
      // prevent prev on register
      if (dir === 'prev' && currentPage === 'AssessmentProfile') {
        i = 0;
      }
      // check to skip pages
      if (pageList[i] && pageList[i].conditional) {
        // page is conditional
        if (auth.access === '2') {
          // condition not met - skip
          if (dir === 'prev') {
            i--;
          } else {
            i++;
          }
        }
      }

      // save page we're on
      dispatchValues({
        id: 'iProgress',
        value: i,
      });

      // Save scroll position before navigating forward
      if (dir !== 'prev' && dir !== 'load') {
        const rightContainer = document.getElementById('right_container');
        if (rightContainer) {
          setScrollPositions((prev) => ({
            ...prev,
            [currentPage]: rightContainer.scrollTop,
          }));
        }
      }

      // fadeout current page
      if (dir !== 'load') {
        document.getElementById(currentPage).classList.remove('active');
        if (currentMedia !== pageList[i].media) {
          document.getElementById(currentMedia).classList.remove('active');
        }
      }
      setTimeout(() => {
        document.getElementById('right_container').style.width = 'auto';
        // switch content
        dispatch({
          type: 'PAGE',
          title: pageList[i].title,
        });
        if (pageOptions.currentMedia !== pageList[i].media) {
          dispatch({
            type: 'MEDIA',
            media: pageList[i].media,
          });
        }
      }, 500);
      // fadein new page
      setTimeout(() => {
        if (document.getElementById(pageList[i].title)) {
          document.getElementById(pageList[i].title).classList.add('active');
        }
        if (document.getElementById(pageList[i].media) && currentMedia !== pageList[i].media) {
          document.getElementById(pageList[i].media).classList.add('active');
        }
        // Handle scroll position
        const rightContainer = document.getElementById('right_container');
        if (rightContainer) {
          if (dir === 'prev') {
            // Restore saved scroll position when going back
            const savedScrollPosition = scrollPositions[pageList[i].title];
            const scrollTop = savedScrollPosition !== undefined ? savedScrollPosition : 0;
            rightContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
          } else if (dir !== 'load') {
            // Scroll to top when moving forward
            rightContainer.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }
        // Focus management after page transition
        let focusTarget: HTMLElement | null = null;

        if (dir === 'prev') {
          const forwardButton = document.getElementById('next') as HTMLButtonElement;
          if (forwardButton && !forwardButton.disabled) {
            focusTarget = forwardButton;
          }
        }

        // Fall back to first focusable element if no specific target
        if (!focusTarget) {
          focusTarget = document
            .getElementById('main_container')
            ?.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ) as HTMLElement;
        }

        if (focusTarget) {
          focusTarget.focus();
        }

        // animate top progress bar
        const progressStep = Math.round((100 / pageList.length) * (i + 1) * 0.88 + 12);
        dispatch({
          type: 'PROGRESS',
          progress: progressStep,
        });
      }, 700);
      // change bottom progress bar
      dispatch({
        type: 'SECTION',
        no: pageList[i].sectionNo,
        max: pageList[i].sectionMax,
      });
    }
  };

  const getAssessmentDataHandler = async () => {
    if (auth.userId && auth.token) {
      let responseData;
      try {
        responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/assessments/uid/${auth.userId}/web`
        );
      } catch (err) {
        if (err instanceof HttpRequestError && err.errorCode === 'FULL_ASSESSMENT_NOT_AVAILABLE') {
          setMobileOnly(true);
          return;
        }
        if (err instanceof HttpRequestError && err.errorCode === 'PLATFORM_MISMATCH') {
          setIsPlatformMismatch(true);
          return;
        }
      }
      let data;
      if (!responseData) {
        return false;
      }
      if (!responseData.editable) {
        setIsRetakeEnabled(true);
        return;
      }

      setAssessmentId(responseData.id);
      // set regional content
      setRegionContent(responseData.regional_content);
      if (responseData.data !== 'empty') {
        data = normalizeFromApi(responseData.data);
      }

      let iProgress = -1;
      if (data) {
        if (data.iProgress) {
          iProgress = data.iProgress as number;
        }
        Object.keys(data).forEach((key) => {
          dispatchValues({
            id: key,
            value: data[key],
          });
          return key;
        });
      }
      navigateNextPageHandler('load', iProgress);
    }
  };

  const navigatePrevPageHandler = () => {
    navigateNextPageHandler('prev');
  };

  const openModal = useCallback(
    (props) => {
      setModalIsOpen(true);
      setModalTextContent(props);
    },
    [modalIsOpen, modalTextContent]
  );

  useEffect(() => {
    if (auth.isLoggedIn) {
      getAssessmentDataHandler();
    }
  }, [auth.isLoggedIn]);

  const quickNavHandler = useCallback((props) => {
    navigateNextPageHandler('load', props);
  }, []);

  return {
    assessmentValues,
    errorSummary,
    mobileOnly,
    isPlatformMismatch,
    isRetakeEnabled,
    pageOptions,
    regionContent,
    repeatAssessment,
    navigateNextPageHandler,
    navigatePrevPageHandler,
    openModal,
    quickNavHandler,
    saveAssessmentValue,
  };
}
